import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Zap, TrendingUp, Globe, ExternalLink, RefreshCw, Loader2, Search,
  CheckCircle, Clock, Newspaper, Send, BarChart3
} from "lucide-react";
import { ModuleDescription } from "@/components/ModuleDescription";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PC_STATUSES = { not_submitted: "Nie zgloszona", submitted: "Zgloszona", approved: "Zatwierdzona", rejected: "Odrzucona" };
const PC_COLORS = { not_submitted: "bg-gray-100 text-gray-600", submitted: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-600" };

export default function BreakingNews() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allSites, setAllSites] = useState([]);
  const [siteSearch, setSiteSearch] = useState("");

  const fetchData = async () => {
    try {
      const [res, sitesRes] = await Promise.all([
        axios.get(`${API}/breaking-news/dashboard`),
        axios.get(`${API}/breaking-news/all-sites`),
      ]);
      setData(res.data);
      setAllSites(sitesRes.data || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const toggleSiteFlag = async (siteId, field, value) => {
    try {
      await axios.put(`${API}/sites/${siteId}/google-news-status`, {[field]: value});
      setAllSites(prev => prev.map(s => s.id === siteId ? {...s, [field]: value} : s));
      toast.success("Zapisano");
    } catch(e) { toast.error("Blad"); }
  };

  const updatePCStatus = async (siteId, status) => {
    try {
      await axios.put(`${API}/sites/${siteId}/publisher-center-status`, { status });
      toast.success("Zapisano");
      fetchData();
    } catch(e) { toast.error("Blad"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="breaking-news-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Zap className="w-8 h-8 text-amber-500" /> Breaking News</h1>
          <p className="text-muted-foreground text-sm mt-1">Gorce trendy, automatyczne newsy, Google News & Discover</p>
        </div>
      <ModuleDescription title="Jak dziala Breaking News?">
        <p><strong>Breaking News</strong> monitoruje Google Trends co 15 minut i automatycznie publikuje gorące newsy na stronach informacyjnych.</p>
        <p><strong>Jak to dziala:</strong> Agent pobiera trending topics z Google Trends RSS, parsuje zrodla z RSS i Wikipedii (aby uniknac zmyslania faktow), generuje artykul przez AI i natychmiast publikuje.</p>
        <p><strong>Zdjecia</strong> sa pobierane z oryginalnych zrodel (RSS feed images) i skalowane na serwerze.</p>
        <p><strong>Indeksowanie:</strong> Po publikacji system automatycznie pinguje Google Indexing API i Bing IndexNow aby przyspieszyc indeksacje.</p>
      </ModuleDescription>

        <Button size="sm" onClick={fetchData} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Odswierz</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{data?.stats?.bn_today || 0}</p>
          <p className="text-xs text-muted-foreground">Newsow dzisiaj</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{data?.stats?.bn_week || 0}</p>
          <p className="text-xs text-muted-foreground">Newsow (7 dni)</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{data?.stats?.gn_sites || 0}</p>
          <p className="text-xs text-muted-foreground">Stron w Google News</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{data?.stats?.gd_sites || 0}</p>
          <p className="text-xs text-muted-foreground">Stron w Discover</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trending teraz */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" /> Trending teraz</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[350px]">
              {data?.trends?.length > 0 ? data.trends.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b last:border-0 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-5">{i+1}.</span>
                    <span className="font-medium">{t.title}</span>
                  </div>
                  <Badge variant="secondary" className="text-[9px]">{t.traffic || "?"}</Badge>
                </div>
              )) : <p className="text-xs text-muted-foreground text-center py-8">Brak trendow</p>}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Breaking News dzisiaj + tydzien */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Newspaper className="w-4 h-4 text-amber-500" /> Breaking News ({data?.breaking_week?.length || 0} w tym tygodniu)</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[350px]">
              {data?.breaking_week?.length > 0 ? data.breaking_week.map((bn, i) => (
                <div key={i} className="border rounded-lg p-3 mb-2 text-xs">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{bn.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                        <span>{bn.site_name}</span>
                        {bn.google_news && <Badge className="text-[8px] bg-green-100 text-green-700 px-1">GN</Badge>}
                        {bn.google_discover && <Badge className="text-[8px] bg-blue-100 text-blue-700 px-1">GD</Badge>}
                        {bn.trending_traffic && <Badge variant="outline" className="text-[8px]">{bn.trending_traffic} wyszukiwan</Badge>}
                        <span>{new Date(bn.created_at).toLocaleString("pl-PL")}</span>
                      </div>
                    </div>
                    {bn.published_url && (
                      <a href={bn.published_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 ml-2">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              )) : <p className="text-xs text-muted-foreground text-center py-8">Brak breaking news</p>}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Info o priorytetach */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs space-y-1">
              <p className="font-bold text-sm">Jak dziala priorytet Breaking News:</p>
              <p><strong className="text-green-700">1. Google News</strong> - strony z GN dostaja newsy PIERWSZE (najszybsze indeksowanie, max ruch)</p>
              <p><strong className="text-blue-700">2. Google Discover</strong> - drugie w kolejce</p>
              <p><strong className="text-gray-600">3. Inne strony</strong> - fallback, agent rozwija je regularnym contentem</p>
              <p className="text-muted-foreground mt-1">GN/GD ustawiaj RECZNIE - tylko strony ktore naprawde sa w Google News/Discover (sprawdz w publishercenter.google.com i Search Console)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Strony newsowe - pelna lista z checkboxami */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Globe className="w-4 h-4" /> Strony newsowe & Google News & Discover</CardTitle>
            <a href="https://publishercenter.google.com" target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="outline" className="gap-1.5 text-xs"><ExternalLink className="w-3 h-3" /> Publisher Center</Button>
            </a>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
              <Input placeholder="Szukaj strony..." value={siteSearch} onChange={e => setSiteSearch(e.target.value)} className="h-8 text-xs pl-8" />
            </div>
            <div className="flex gap-3 text-[10px] text-muted-foreground">
              <span className="text-amber-600 font-bold">News: {allSites.filter(s=>s.news_site).length}</span>
              <span className="text-green-600 font-bold">GN: {allSites.filter(s=>s.google_news).length}</span>
              <span className="text-blue-600 font-bold">GD: {allSites.filter(s=>s.google_discover).length}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <ScrollArea className="h-[350px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white dark:bg-gray-950">
                <tr className="border-b text-left">
                  <th className="py-2 font-medium">Strona</th>
                  <th className="py-2 font-medium text-center" title="Strona newsowa - Breaking News">News</th>
                  <th className="py-2 font-medium text-center" title="Google News">GN</th>
                  <th className="py-2 font-medium text-center" title="Google Discover">GD</th>
                  <th className="py-2 font-medium">Kategoria</th>
                </tr>
              </thead>
              <tbody>
                {allSites.filter(s => !siteSearch || s.name?.toLowerCase().includes(siteSearch.toLowerCase())).map((site, i) => (
                  <tr key={i} className={`border-b hover:bg-muted/30 ${site.news_site || site.google_news ? "bg-amber-50/30" : ""}`}>
                    <td className="py-1.5 font-medium">{site.name}</td>
                    <td className="py-1.5 text-center"><input type="checkbox" className="w-3.5 h-3.5 rounded accent-amber-500" checked={site.news_site || false} onChange={e => toggleSiteFlag(site.id, "news_site", e.target.checked)} /></td>
                    <td className="py-1.5 text-center"><input type="checkbox" className="w-3.5 h-3.5 rounded accent-green-500" checked={site.google_news || false} onChange={e => toggleSiteFlag(site.id, "google_news", e.target.checked)} /></td>
                    <td className="py-1.5 text-center"><input type="checkbox" className="w-3.5 h-3.5 rounded accent-blue-500" checked={site.google_discover || false} onChange={e => toggleSiteFlag(site.id, "google_discover", e.target.checked)} /></td>
                    <td className="py-1.5"><input type="text" className="w-full text-xs border-0 bg-transparent text-muted-foreground focus:bg-white focus:border focus:rounded focus:px-1 focus:text-foreground" defaultValue={site.category || ""} placeholder="Kategoria..." onBlur={async (e) => { const v = e.target.value.trim(); if (v && v !== site.category) { try { await axios.put(`${API}/sites/${site.id}/category`, {category: v}); setAllSites(prev => prev.map(s => s.id === site.id ? {...s, category: v} : s)); toast.success(`${site.name}: ${v}`); } catch(err) { toast.error("Blad"); } } }} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Indexing logs */}
      {data?.indexing_logs?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><Send className="w-4 h-4" /> Ostatnie indeksowania</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {data.indexing_logs.map((log, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b">
                  <span className="truncate flex-1">{log.url}</span>
                  <div className="flex gap-2 text-muted-foreground shrink-0">
                    {log.results?.google?.success && <Badge className="text-[8px] bg-green-100 text-green-700">Google OK</Badge>}
                    {log.results?.indexnow?.success && <Badge className="text-[8px] bg-blue-100 text-blue-700">Bing OK</Badge>}
                    <span>{new Date(log.indexed_at).toLocaleString("pl-PL")}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
